import { zodResolver } from '@hookform/resolvers/zod';
import {
  AssociationEntity,
  ContactRequestSchema,
  ContactStatusEnum,
  IContactRequestSchema,
  IContactRespSchema,
} from '@monetize/types/app';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useGetAccountById } from '../../api/accountsService';
import { handleApiErrorToast } from '../../api/axios';
import { useCreateContact, useUpdateContact } from '../../api/contactsService';
import { useGetById } from '../../api/queryUtils';
import { CONTACT_STATUS_DISPLAY } from '../../constants/contacts';
import { getAccountContactsRoute } from '../../constants/routes';
import { useCtrlEnterHotkey } from '../../hooks/useHotkeys';
import { getCountryCodeFromNameOrCode } from '../../services/countryState';
import { logger } from '../../services/logger';
import { ExternalAssociationsIcons } from '../ExternalAssociations/ExternalAssociationsIcons';
import {
  MBox,
  MButton,
  MCenterModal,
  MPageHeader,
  MPageLoader,
  MStack,
  MTag,
} from '../Monetize';
import { ContactForm } from './ContactForm';

const formDefault: Partial<IContactRequestSchema> = {
  fullName: '',
  email: '',
  phone: null,
  address: {
    city: null,
    country: null,
    line1: null,
    line2: null,
    state: null,
    postalCode: null,
  },
  status: ContactStatusEnum.ACTIVE,
  primary: false,
  customFields: {},
};

interface ContactFormModalProps {
  isOpen: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  accountId?: string | null;
  contactId?: string | null;
  onClose: (contact?: IContactRespSchema) => void;
  onUpdatedContact?: null | ((contact: IContactRespSchema) => void);
  onCreatedContact?: null | ((contact: IContactRespSchema) => void);
  isReadOnly?: boolean;
  defaultValues?: Partial<IContactRequestSchema>;
}

export const ContactFormModal = React.forwardRef(
  (
    {
      isOpen,
      contactId,
      accountId,
      isLoading,
      isDisabled,
      onClose,
      onUpdatedContact,
      onCreatedContact,
      isReadOnly,
      defaultValues,
    }: ContactFormModalProps,
    ref,
  ) => {
    const params = useParams();
    const [editContactId, setEditContactId] = useState<string>(() => {
      if (contactId) {
        return contactId;
      } else if (params?.contactId) {
        return params?.contactId;
      }
      return '';
    });

    useEffect(() => {
      if (contactId) {
        setEditContactId(contactId);
      } else if (params?.contactId) {
        setEditContactId(params?.contactId);
      }
    }, [isOpen, params, contactId]);

    const { isLoading: isContactFetchLoading, data: contact } =
      useGetById<IContactRespSchema>('contacts', editContactId!, {
        enabled: !!editContactId,
        refetchOnWindowFocus: false,
        select: (data) => {
          if (!data.address) {
            data.address = {
              city: null,
              country: null,
              line1: null,
              line2: null,
              state: null,
              postalCode: null,
            };
          }
          // Attempt to normalize the country to the 2-digit code
          if (data.address.country) {
            data.address.country = getCountryCodeFromNameOrCode(
              data.address.country,
            );
          }
          return { ...data };
        },
        meta: {
          showErrorToast: true,
          navigateTo: accountId
            ? getAccountContactsRoute(accountId)
            : undefined,
        },
      });

    const isPrimary = !!contact?.primary;

    const formMethods = useForm<IContactRequestSchema>({
      resolver: zodResolver(ContactRequestSchema),
      mode: 'onBlur',
      defaultValues: contact || { ...formDefault, ...defaultValues },
    });

    const {
      handleSubmit,
      control,
      formState: { errors },
      trigger,
      reset,
      setValue,
      watch,
    } = formMethods;

    useEffect(() => {
      if (contact) {
        const contactWithDefaultAddress = {
          ...contact,
          address: contact.address || formDefault.address,
        };
        reset(Object.assign({}, formDefault, contactWithDefaultAddress));
      }
    }, [contact, reset]);

    const { mutate: doCreateContact, isPending: createContactLoading } =
      useCreateContact({
        onSuccess: (data) => {
          onClose(data);
          onCreatedContact && onCreatedContact(data);
        },
        meta: { showErrorToast: true },
      });

    const { mutate: doUpdateContact, isPending: updateContactLoading } =
      useUpdateContact({
        onSuccess: (data) => {
          onClose();
          onUpdatedContact && onUpdatedContact(data);
        },
        meta: { showErrorToast: true },
      });

    const isContactLoading =
      !!isOpen && !!editContactId && isContactFetchLoading;

    const isContactSaving = createContactLoading || updateContactLoading;

    const isValid = Object.keys(errors).length === 0;

    // Reset form when modal closes
    useEffect(() => {
      if (!isOpen) {
        reset({ ...formDefault });
      }
    }, [isOpen, reset]);

    useCtrlEnterHotkey(() => {
      if (isOpen) {
        handleSubmit(onSubmit, onError)();
      }
    });

    const onSubmit = async (requestData: IContactRequestSchema) => {
      try {
        if (!editContactId) {
          if (accountId) {
            doCreateContact({
              accountId,
              payload: requestData,
            });
          }
        } else {
          doUpdateContact({
            contactId: editContactId,
            payload: requestData,
          });
        }
      } catch (err) {
        handleApiErrorToast(err);
      }
    };
    const onError = (errs: any, event: any) => {
      logger.warn(`ERRORS:`, errs);
    };

    const { data: accountDetails } = useGetAccountById(accountId as string, {
      enabled: !!accountId,
    });

    const title = (
      <MPageHeader
        title={
          contactId || params?.contactId
            ? `${!isReadOnly ? 'Edit' : 'View'} Contact`
            : 'New Contact'
        }
        status={
          contact?.status ? CONTACT_STATUS_DISPLAY[contact?.status] : undefined
        }
        tag={
          <MBox display="flex">
            {isPrimary && <MTag variant="blue">Primary</MTag>}
            {contact?.id && (
              <ExternalAssociationsIcons
                key="ExternalAssociationsIcons"
                entity={AssociationEntity.CONTACT}
                recordId={contact.id}
              />
            )}
          </MBox>
        }
        parentLink={
          accountId || contact?.accountId
            ? getAccountContactsRoute(accountId || contact!.accountId)
            : undefined
        }
        parentLinkTitle={accountDetails?.accountName}
        copyUrl
        id={contact?.id}
        containerProps={{ mb: 0 }}
        recordAuditInfo={contact}
        entityJson={contact}
      ></MPageHeader>
    );

    return (
      <FormProvider {...formMethods}>
        <MCenterModal
          isOpen={isOpen}
          onClose={onClose}
          renderModalTitleActions={() => title}
          closeOnEsc={false}
          closeOnOverlayClick={false}
          renderFooter={
            isContactLoading || isReadOnly
              ? null
              : () => (
                  <MStack
                    gap={4}
                    direction="row"
                    align="center"
                    justify="right"
                    flex={1}
                  >
                    <MButton
                      onClick={() => onClose()}
                      variant="cancel"
                      minW="auto"
                    >
                      Cancel
                    </MButton>
                    <MButton
                      data-testid="contact-form-submit"
                      form="contact-form"
                      variant="primary"
                      isLoading={
                        isContactLoading ||
                        isContactSaving ||
                        isLoading ||
                        createContactLoading ||
                        updateContactLoading
                      }
                      onClick={handleSubmit(onSubmit, onError)}
                      isDisabled={!isValid || isDisabled}
                      type="submit"
                      minW="auto"
                    >
                      Save
                    </MButton>
                  </MStack>
                )
          }
          modalContentProps={{ 'data-testid': 'contact-form' } as any}
        >
          <MBox>
            {isContactLoading ? (
              <MPageLoader height="450px" />
            ) : (
              <ContactForm
                formId="contact-form"
                isDisabled={isDisabled}
                control={control}
                errors={errors}
                watch={watch}
                trigger={trigger}
                setValue={setValue}
                handleSubmit={handleSubmit(onSubmit, onError)}
                isReadOnly={isReadOnly}
                contactId={contactId || params?.contactId}
                omitPrimary={contact?.status !== ContactStatusEnum.ACTIVE}
              />
            )}
          </MBox>
        </MCenterModal>
      </FormProvider>
    );
  },
);
